import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { useState, useRef } from "react";
import fields from "./inquiryFormFields";
import UploadcareUploader from "../UploadcareUploader/UploadcareUploader";
import InquiryModal from "./InquiryModal/InquiryModal";
import useFormValidation from "../../hooks/useFormValidation";
import subscribeToBrevo from "../../services/brevo";
import styles from "./InquiryForm.module.css";
import PhoneNumberInput from "./PhoneNumberInput";
import SizeRuler, { formatTattooSize } from "./SizeRuler";

const initialFormState = {
  fullName: "",
  pronouns: "",
  phoneNumber: "",
  email: "",
  emailMarketingConsent: true,
  instagram: "",
  flashOrCustom: "",
  flashImage: "",
  placement: "",
  tattooSize: "",
  tattooSizeUnit: "in",
  colorOrBW: "",
  customDescription: "",
  referenceImages: ["", "", ""],
  preferredDays: [],
  preferredTimes: [],
  unavailableDates: "",
  currentLocation: "",
  budget: "",
  tattooedBefore: "",
  bookingForOthers: "",
  additionalQuestions: "",
};

const InquiryForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [popup, setPopup] = useState({ message: "", type: "" });
  const formRef = useRef(null);
  const { errors, validate } = useFormValidation(fields, formData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => {
      const current = prev[name] || [];
      const updated = checked
        ? [...current, value]
        : current.filter((item) => item !== value);
      return { ...prev, [name]: updated };
    });
  };

  const handleUpload = (name, url) => {
    if (name.startsWith("referenceImage-")) {
      const index = parseInt(name.split("-")[1], 10);
      const updated = [...formData.referenceImages];
      updated[index] = url;
      setFormData((prev) => ({ ...prev, referenceImages: updated }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: url }));
    }
  };

  const handleEmailMarketingConsent = (e) => {
    setFormData((prev) => ({
      ...prev,
      emailMarketingConsent: e.target.checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setPopup({
        message: "Please fill out all required fields marked with *.",
        type: "error",
      });
      return;
    }

    try {
      const inquiryData = {
        ...formData,
        tattooSize: formatTattooSize(formData.tattooSize, formData.tattooSizeUnit),
        createdAt: Timestamp.now(),
        read: false,
      };

      await addDoc(collection(db, "inquiries"), inquiryData);
      if (formData.emailMarketingConsent) {
        try {
          await subscribeToBrevo(formData.email);
        } catch (brevoError) {
          console.error(
            "Inquiry saved, but Brevo subscription failed:",
            brevoError,
          );
        }
      }
      setPopup({
        message: "Submission successful! Thank you ✨",
        type: "success",
      });
      setFormData(initialFormState);

      setTimeout(() => {
        setPopup({ message: "", type: "" });
      }, 4000);

      if (formRef.current) {
        const iframes = formRef.current.querySelectorAll("iframe");
        iframes.forEach((iframe) => {
          iframe.contentWindow?.postMessage("uploadcare-widget-reset", "*");
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setPopup({
        message: "Something went wrong. Please try again.",
        type: "error",
      });
    }
  };

  const closePopup = () => setPopup({ message: "", type: "" });

  return (
    <form
      className={styles.form}
      onSubmit={handleSubmit}
      ref={formRef}
      noValidate
    >
      <h1 className={styles.title}>
        <span className={styles.titleSparkle} aria-hidden="true">
          ✨
        </span>
        <span className={styles.titleText}>Appointment Request</span>
        <span className={styles.titleSparkle} aria-hidden="true">
          ✨
        </span>
      </h1>
      <p className={styles.subtitle}>
        Marléna / Fineline Tattoo Albuquerque, NM
      </p>
      {fields.map((field) => {
        const {
          name,
          label,
          type,
          required,
          placeholder,
          options,
          description,
          condition,
        } = field;

        if (condition) {
          const { field: conditionField, value } = condition;
          if (formData[conditionField] !== value) return null;
        }

        if (name === "tattooSize") {
          return (
            <SizeRuler
              key={name}
              value={formData[name]}
              unit={formData.tattooSizeUnit}
              onUnitChange={(unit) => setFormData((prev) => ({ ...prev, tattooSizeUnit: unit }))}
              onChange={(value) => setFormData((prev) => ({ ...prev, [name]: value }))}
              error={errors[name]}
            />
          );
        }

        if (name === "phoneNumber") {
          return (
            <PhoneNumberInput
              key={name}
              value={formData[name]}
              onChange={(value) => setFormData((prev) => ({ ...prev, [name]: value }))}
              required={required}
              error={errors[name]}
            />
          );
        }

        if (type === "uploadcare-single") {
          return (
            <div key={name}>
              <UploadcareUploader
                label={label}
                name={name}
                value={formData[name]}
                onUpload={handleUpload}
              />
              {description && <p className={styles.subtext}>{description}</p>}
              {errors[name] && <p className={styles.error}>{errors[name]}</p>}
            </div>
          );
        }

        if (type === "uploadcare-multiple") {
          return (
            <div key={name}>
              <label>{label}</label>
              {description && <p className={styles.subtext}>{description}</p>}
              <div className={styles.referenceImages}>
                {formData.referenceImages.map((url, index) => (
                  <UploadcareUploader
                    key={index}
                    label={`Reference Image ${index + 1}`}
                    name={`referenceImage-${index}`}
                    value={url}
                    onUpload={handleUpload}
                  />
                ))}
              </div>
              {errors[name] && <p className={styles.error}>{errors[name]}</p>}
            </div>
          );
        }

        if (type === "radio") {
          return (
            <fieldset key={name}>
              <legend>{label}</legend>
              {description && <p className={styles.subtext}>{description}</p>}
              <div className={styles.radioGroup}>
                {options.map((option) => (
                  <label key={option}>
                    <input
                      type="radio"
                      name={name}
                      value={option}
                      checked={formData[name] === option}
                      onChange={handleChange}
                      required={required}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors[name] && <p className={styles.error}>{errors[name]}</p>}
            </fieldset>
          );
        }

        if (type === "checkbox") {
          return (
            <fieldset key={name}>
              <legend>{label}</legend>
              {description && <p className={styles.subtext}>{description}</p>}
              <div className={styles.checkboxGroup}>
                {options.map((option) => (
                  <label key={option}>
                    <input
                      type="checkbox"
                      name={name}
                      value={option}
                      checked={formData[name]?.includes(option)}
                      onChange={handleCheckboxChange}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {errors[name] && <p className={styles.error}>{errors[name]}</p>}
            </fieldset>
          );
        }

        return (
          <div key={name}>
            <label htmlFor={name}>{label}</label>
            {description && <p className={styles.subtext}>{description}</p>}
            {type === "textarea" ? (
              <textarea
                id={name}
                name={name}
                value={formData[name]}
                onChange={handleChange}
                required={required}
              />
            ) : (
              <input
                id={name}
                name={name}
                type={type}
                placeholder={placeholder || ""}
                value={formData[name]}
                onChange={handleChange}
                required={required}
              />
            )}

            {name === "email" && formData.email.includes("@") && (
              <label className={styles.emailMarketing}>
                <input
                  type="checkbox"
                  checked={formData.emailMarketingConsent}
                  onChange={handleEmailMarketingConsent}
                />
                <p className={styles.subtext}>
                  Subscribe me to Marléna's email list for updates, booking
                  announcements, and new work.
                </p>
              </label>
            )}
            {errors[name] && <p className={styles.error}>{errors[name]}</p>}
          </div>
        );
      })}

      <button type="submit" className={styles.submit}>
        Submit
      </button>

      {popup.message && (
        <InquiryModal
          message={popup.message}
          type={popup.type}
          onClose={closePopup}
        />
      )}
    </form>
  );
};

export default InquiryForm;
